import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import client from "../../../shared/api/client";
import {
  Alert, Button, Card, CardHeader, CellText, Checkbox, DotsVertical, Edit01, EmptyState, Menu, Modal, Plus, Select, Table, TextInput, Trash01,
} from "@opendate-app/setlist";
import { errorText, toastError } from "../../../shared/toastHelper";

const BASES = [
  { key: "apply_to_ticket_price", label: "Ticket, add-on and pass prices", summary: "ticket, add-on and pass prices" },
  { key: "apply_to_venue_royalty", label: "Organizer fee", summary: "the organizer fee" },
  { key: "apply_to_opendate_service_fee", label: "Opendate fee", summary: "the Opendate fee" },
];

const BLANK_DRAFT = {
  name: "",
  rate: "",
  apply_to_ticket_price: true,
  apply_to_venue_royalty: true,
  apply_to_opendate_service_fee: false,
  general_ledger_code_id: "",
  venue_ownership_ids: [],
};

const draftFrom = (tax) => ({
  name: tax.name || "",
  rate: tax.rate == null ? "" : String(parseFloat(tax.rate)),
  apply_to_ticket_price: Boolean(tax.apply_to_ticket_price),
  apply_to_venue_royalty: Boolean(tax.apply_to_venue_royalty),
  apply_to_opendate_service_fee: Boolean(tax.apply_to_opendate_service_fee),
  general_ledger_code_id: tax.general_ledger_code_id == null ? "" : String(tax.general_ledger_code_id),
  venue_ownership_ids: tax.venue_ownership_ids || [],
});

const codeLabel = (code) => `${code.code} — ${code.name}`;

const sameIds = (a, b) => a.length === b.length && a.every((id) => b.includes(id));

const STOP_CHARGING_HINT = "Already recorded on past sales. Untick its venues to stop charging it.";
const UNLISTED_VENUES_HINT = " For venues not listed here, contact Opendate support.";

const ManualTaxes = ({ team, initialTaxes, venueOwnerships }) => {
  const canAccessGeneralLedgerCodes = team.can_access_general_ledger_codes ?? false;
  const [taxes, setTaxes] = useState(initialTaxes);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(BLANK_DRAFT);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [generalLedgerCodes, setGeneralLedgerCodes] = useState([]);

  useEffect(() => {
    if (!canAccessGeneralLedgerCodes) return undefined;
    let ignore = false;
    client.get(`/teams/${team.id}/general_ledger_codes`)
      .then(({ data }) => { if (!ignore) setGeneralLedgerCodes(data || []); })
      .catch(() => { if (!ignore) toastError("Could not load GL codes. Reload the page to try again."); });
    return () => { ignore = true; };
  }, [team.id, canAccessGeneralLedgerCodes]);

  const venueNames = Object.fromEntries(venueOwnerships.map((venueOwnership) => [venueOwnership.id, venueOwnership.name]));
  const unlistedVenueIds = (tax) => tax.venue_ownership_ids.filter((id) => !venueNames[id]);

  const codeOptions = [{ value: "", label: "No GL code" }]
    .concat(generalLedgerCodes.map((code) => ({ value: String(code.id), label: codeLabel(code) })));
  if (editing && editing.general_ledger_code && !generalLedgerCodes.some((code) => code.id === editing.general_ledger_code_id)) {
    const suffix = editing.general_ledger_code.archived ? " (archived)" : "";
    codeOptions.push({ value: String(editing.general_ledger_code_id), label: `${codeLabel(editing.general_ledger_code)}${suffix}` });
  }

  const open = (tax) => {
    setEditing(tax || {});
    const onlyVenueIds = venueOwnerships.length === 1 ? [venueOwnerships[0].id] : [];
    setDraft(tax ? draftFrom(tax) : { ...BLANK_DRAFT, venue_ownership_ids: onlyVenueIds });
    setError(null);
  };

  const set = (key) => (e) => { const { value } = e.target; setDraft((current) => ({ ...current, [key]: value })); };
  const toggle = (key) => (e) => { const { checked } = e.target; setDraft((current) => ({ ...current, [key]: checked })); };
  const toggleVenue = (id) => (e) => {
    const { checked } = e.target;
    setDraft((current) => ({
      ...current,
      venue_ownership_ids: checked
        ? [...current.venue_ownership_ids, id]
        : current.venue_ownership_ids.filter((venueOwnershipId) => venueOwnershipId !== id),
    }));
  };
  const canSubmit = draft.name.trim() !== "" && draft.rate !== "";

  const save = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      const { general_ledger_code_id: generalLedgerCodeId, venue_ownership_ids: venueOwnershipIds, ...fields } = draft;
      const venuesChanged = !editing.id || !sameIds(venueOwnershipIds, editing.venue_ownership_ids || []);
      const body = {
        manual_tax: {
          ...fields,
          ...(canAccessGeneralLedgerCodes ? { general_ledger_code_id: generalLedgerCodeId || null } : {}),
          ...(venuesChanged ? { venue_ownership_ids: venueOwnershipIds } : {}),
        },
      };
      const { data } = editing.id
        ? await client.patch(`/teams/${team.id}/manual_taxes/${editing.id}`, body)
        : await client.post(`/teams/${team.id}/manual_taxes`, body);
      setTaxes((current) => [...current.filter((tax) => tax.id !== data.id), data].sort((a, b) => a.name.localeCompare(b.name)));
      setEditing(null);
    } catch (e) {
      setError(errorText(e, "Could not save this tax. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const destroy = async () => {
    const tax = deleting;
    setDeleting(null);
    try {
      await client.delete(`/teams/${team.id}/manual_taxes/${tax.id}`);
      setTaxes((current) => current.filter((other) => other.id !== tax.id));
    } catch (e) {
      if (e.response?.status === 422 && Array.isArray(e.response.data)) {
        setTaxes((current) => current.map((other) => (other.id === tax.id ? { ...other, deletable: false } : other)));
      }
      toastError(errorText(e, "Could not delete this tax. Please try again."));
    }
  };

  const columns = [
    {
      key: "name",
      header: "Tax",
      primary: true,
      render: (tax) => {
        const bases = BASES.filter((base) => tax[base.key]).map((base) => base.summary);
        return <CellText primary={tax.name} supporting={bases.length ? `Charged on ${bases.join(", ")}` : "Not charged on anything"} />;
      },
    },
    { key: "rate", header: "Rate", align: "right", render: (tax) => `${parseFloat(tax.rate)}%` },
    {
      key: "venues",
      header: "Venues",
      render: (tax) => {
        if (tax.venue_ownership_ids.length === 0) return "None, so it isn't charged";
        const names = tax.venue_ownership_ids.map((id) => venueNames[id]).filter(Boolean);
        const otherCount = unlistedVenueIds(tax).length;
        const others = otherCount > 0 ? [`${otherCount} other venue${otherCount === 1 ? "" : "s"}`] : [];
        return names.concat(others).join(", ");
      },
    },
    ...(canAccessGeneralLedgerCodes ? [{
      key: "general_ledger_code",
      header: "GL code",
      render: (tax) => (tax.general_ledger_code ? codeLabel(tax.general_ledger_code) : "Unassigned"),
    }] : []),
    {
      key: "actions",
      header: "",
      align: "right",
      render: (tax) => (
        <Menu
          placement="bottom-end"
          items={[
            { label: "Edit tax", icon: Edit01, onClick: () => open(tax) },
            tax.deletable
              ? { label: "Delete tax", icon: Trash01, destructive: true, onClick: () => setDeleting(tax) }
              : { label: "Delete tax", icon: Trash01, disabled: true, description: STOP_CHARGING_HINT + (unlistedVenueIds(tax).length ? UNLISTED_VENUES_HINT : "") },
          ]}
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${tax.name}`} />}
        />
      ),
    },
  ];

  return (
    <div className="mt-4" data-testid="manual-taxes">
      <Card
        padding="none"
        header={(
          <CardHeader
            title="Sales taxes"
            supportingText="Taxes added to what buyers pay at checkout. Each tax is charged at the venues you tick. Check the rates and what they apply to with your accountant."
            actions={(
              <Button variant="secondary" icon={Plus} onClick={() => open(null)} data-testid="manual-tax-new">
                Add tax
              </Button>
            )}
          />
        )}
      >
      <Table
        columns={columns}
        data={taxes}
        getRowKey={(tax) => tax.id}
        stackBelow={720}
        empty={<EmptyState size="sm" title="No sales taxes yet" description="Add one if you collect sales or admission tax on tickets." />}
      />
      </Card>

      <Modal
        open={editing !== null}
        onClose={() => { if (!saving) setEditing(null); }}
        icon={false}
        title={editing && editing.id ? "Edit sales tax" : "New sales tax"}
        footer={(
          <>
            <Button onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={saving || !canSubmit} data-testid="manual-tax-save">
              {saving ? "Saving…" : "Save tax"}
            </Button>
          </>
        )}
      >
        {error && <div className="mb-3"><Alert color="error" role="alert" title={error} /></div>}
        <div className="d-flex flex-wrap mb-1">
          <div className="mr-3 mb-2">
            <TextInput label="Name" id="manual-tax-name" maxLength={255} placeholder="e.g. City amusement tax" value={draft.name} onChange={set("name")} />
          </div>
          <div className="mb-2">
            <TextInput label="Rate" id="manual-tax-rate" type="number" min="0" max="100" step="0.001" trailingText="%" value={draft.rate} onChange={set("rate")} />
          </div>
        </div>
        <fieldset className="mb-3">
          <legend className="setlist-text-sm setlist-font-medium mb-2">Charge tax on</legend>
          {BASES.map((base) => (
            <div className="mb-1" key={base.key}>
              <Checkbox id={`manual-tax-${base.key}`} label={base.label} checked={draft[base.key]} onChange={toggle(base.key)} />
            </div>
          ))}
        </fieldset>
        <fieldset className="mb-3" aria-describedby="manual-tax-venues-hint">
          <legend className="setlist-text-sm setlist-font-medium mb-1">Venues</legend>
          <p className="mb-2 text-muted" id="manual-tax-venues-hint">
            Ticking a venue charges this tax there right away, on every ticket type and add-on (including shows already on sale) and on classes. Season passes and packs are ticked on each pass.
          </p>
          {venueOwnerships.map((venueOwnership) => (
            <div className="mb-1" key={venueOwnership.id}>
              <Checkbox
                id={`manual-tax-venue-${venueOwnership.id}`}
                label={venueOwnership.name}
                checked={draft.venue_ownership_ids.includes(venueOwnership.id)}
                onChange={toggleVenue(venueOwnership.id)}
              />
            </div>
          ))}
        </fieldset>
        {canAccessGeneralLedgerCodes && (
          <Select
            label="GL code"
            id="manual-tax-general-ledger-code"
            options={codeOptions}
            value={draft.general_ledger_code_id}
            onChange={(value) => setDraft((current) => ({ ...current, general_ledger_code_id: value || "" }))}
          />
        )}
      </Modal>

      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        type="error"
        title="Delete this tax?"
        description={deleting ? `"${deleting.name}" will be removed from every ticket type and add-on. It hasn't been recorded on any sale yet.` : ""}
        confirmLabel="Delete tax"
        onConfirm={destroy}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
};

ManualTaxes.propTypes = {
  team: PropTypes.shape({ id: PropTypes.number.isRequired, can_access_general_ledger_codes: PropTypes.bool }).isRequired,
  initialTaxes: PropTypes.arrayOf(PropTypes.object).isRequired,
  venueOwnerships: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number.isRequired, name: PropTypes.string })).isRequired,
};

export default ManualTaxes;
